/**
 * Registration utilities for early bird gating.
 *
 * These helpers are shared between EventDetail, EventBrochure, and
 * RegistrationWizard to determine whether the early bird window is
 * active and to manage the short-lived grant token in sessionStorage.
 */

/**
 * Determine whether the early bird registration window is currently active.
 *
 * @param {object} event - The event object from the API (must include
 *   early_bird_enabled, early_bird_opens_at, early_bird_closes_at).
 * @param {Date} [now=new Date()] - Current timestamp (injectable for tests).
 * @returns {boolean}
 */
export function isEarlyBirdActive(event, now = new Date()) {
  if (!event?.early_bird_enabled) return false;
  if (!event?.early_bird_opens_at || !event?.early_bird_closes_at) return false;
  const opens = new Date(event.early_bird_opens_at);
  const closes = new Date(event.early_bird_closes_at);
  return now >= opens && now < closes;
}

/**
 * Read the early bird grant token for an event from sessionStorage.
 *
 * @param {number|string} eventId
 * @returns {string|null} The JWT grant token, or null if not set.
 */
export function getEarlyBirdGrant(eventId) {
  try {
    return sessionStorage.getItem(`lacsched:earlyBirdGrant:${eventId}`) || null;
  } catch {
    return null;
  }
}

/**
 * Store the early bird grant token for an event in sessionStorage.
 *
 * @param {number|string} eventId
 * @param {string} token - The JWT grant token from the verify endpoint.
 */
export function setEarlyBirdGrant(eventId, token) {
  try {
    sessionStorage.setItem(`lacsched:earlyBirdGrant:${eventId}`, token);
  } catch {
    // sessionStorage may be unavailable (private browsing, quota, etc.)
  }
}

/**
 * Remove the early bird grant token for an event from sessionStorage.
 *
 * @param {number|string} eventId
 */
export function clearEarlyBirdGrant(eventId) {
  try {
    sessionStorage.removeItem(`lacsched:earlyBirdGrant:${eventId}`);
  } catch {
    // Ignore — best-effort cleanup
  }
}
